import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, Box, TextField, MenuItem, IconButton, List, ListItem, ListItemAvatar, ListItemText, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CustomCheckBoxV2 from '../../../../components/CheckBoxV2/CheckBox';
import Toast from '../../../../components/Toast/Toast';
import { LANG_TEXT } from '../../../../constants/language';
import { FoodItem } from '../../userMenu.types';
import { useUserMenuContext } from '../../../../context/UserMenuContext';

interface AddRecommendedModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (items: FoodItem[]) => void;
  currentRecommendations: FoodItem[];
  excludeId?: string;
}

export function AddRecommendedModal({ open, onClose, onAdd, currentRecommendations, excludeId }: AddRecommendedModalProps) {
  const { t } = useTranslation();
  const { maxRecommendQuantity, getFoodsByCategoryId, getFoodById, categories } = useUserMenuContext();
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [toast, setToast] = useState<{ open: boolean; variant: 'success' | 'error'; content: string }>({
    open: false,
    variant: 'error',
    content: '',
  });
  const MAX_SELECTED = maxRecommendQuantity ?? 4;

  useEffect(() => {
    if (open) {
      setSelectedItems(currentRecommendations.map((r) => r.id));
    }
  }, [open, currentRecommendations]);

  const allFoods = useMemo(
    () =>
      categories.flatMap((cat: any) =>
        cat.foods.map((food: any) => ({
          id: food.id,
          name: food.name ?? food.food_name ?? '',
          store: food.sku ?? '',
          image: Array.isArray(food.image_url) ? food.image_url[0] : food.image_url,
          price: food.price,
          banner: false,
          enabled: true,
          restaurantId: food.restaurantId || '',
        }))
      )
        .filter((item: any, index: number, self: any[]) => self.findIndex((i: any) => i.id === item.id) === index)
        .filter((item: any) => item.id !== excludeId),
    [categories, excludeId]
  );

  const filteredItems = useMemo(() => {
    if (selectedCategory === 'ALL') return allFoods;
    const foods = getFoodsByCategoryId(selectedCategory);
    const ids = new Set(foods.map((f: any) => f.id));
    return allFoods.filter((item: any) => ids.has(item.id));
  }, [selectedCategory, allFoods, getFoodsByCategoryId]);

  const handleToggleItem = (itemId: string) => {
    setSelectedItems((prev) => {
      const exists = prev.includes(itemId);
      if (exists) return prev.filter((id) => id !== itemId);
      if (prev.length >= MAX_SELECTED) {
        setToast({ open: true, variant: 'error', content: t(LANG_TEXT.ADD_RECOMMEND_LIMIT) });
        return prev;
      }
      return [...prev, itemId];
    });
  };

  const handleConfirm = () => {
    const itemsToAdd = selectedItems
      .map((id) => {
        const food = getFoodById(id);
        if (!food) return null;
        return {
          id: food.id,
          name: food.name ?? food.food_name ?? '',
          store: food.sku ?? '',
          image: Array.isArray(food.image_url) ? food.image_url[0] : food.image_url,
          price: food.price,
          banner: false,
          enabled: true,
          restaurantId: food.restaurantId || '',
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
    onAdd(itemsToAdd);
    setSelectedItems([]);
    onClose();
  };

  const handleClose = () => {
    setSelectedItems([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 2 } }}>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
              }}
              size="small"
              sx={{ flex: 1 }}
            >
              <MenuItem value="ALL">{t(LANG_TEXT.CATEGORIES)}</MenuItem>
              {categories.map((cat: any) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.title}
                </MenuItem>
              ))}
            </TextField>

            <IconButton
              onClick={handleConfirm}
              sx={{
                bgcolor: 'success.main',
                color: 'white',
                height: 36,
                width: 36,
                borderRadius: 1,
                '&:hover': { bgcolor: 'success.dark' },
              }}
            >
              <CheckIcon />
            </IconButton>

            <IconButton
              onClick={handleClose}
              sx={{
                bgcolor: 'error.main',
                height: 36,
                width: 36,
                borderRadius: 1,
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <List sx={{ maxHeight: 400, minHeight: 400, overflow: 'auto' }}>
            {filteredItems.map((item: any) => (
              <ListItem
                key={item.id}
                onClick={() => handleToggleItem(item.id)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  bgcolor: selectedItems.includes(item.id) ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                  cursor: 'pointer',
                }}
              >
                <ListItemAvatar>
                  <Avatar src={item.image} alt={item.name} variant="rounded" sx={{ width: 48, height: 48 }} />
                </ListItemAvatar>
                <ListItemText primary={item.name} secondary={item.store} primaryTypographyProps={{ fontWeight: 500 }} />
                <CustomCheckBoxV2 value={item.id} checked={selectedItems.includes(item.id)} setValue={handleToggleItem} />
              </ListItem>
            ))}
          </List>
          <Toast
            open={toast.open}
            onClose={() => setToast({ ...toast, open: false })}
            variant={toast.variant}
            content={toast.content}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
